import {
  formatParisDate,
  operationKindSchema,
  operationLifecycleSchema,
  OPERATION_KIND,
  OPERATION_LIFECYCLE,
  RENEWAL_DISCOUNT_STATE,
  renewalDiscountStateSchema,
  statusEntries,
  type OperationKind,
  type OperationLifecycle,
  type OperationListItem,
  type RenewalDiscountState,
} from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import type { JSX } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { ListToolbar } from '@/components/data-table/list-toolbar.js';
import { Page, PageHeader } from '@/components/layout/page.js';
import { OriginBadges } from '@/components/filters/origin-badge.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchOperationsList } from './api.js';

const SORTABLE_COLUMNS = ['name', 'updatedAt'] as const;
const NUMERIC_COLUMNS = ['matchedOrdersCount'] as const;
const PAGE_SIZE = 50;
const ALL_LIFECYCLES = '__all__';
const ALL_KINDS = '__all__';
const LIFECYCLE_PREFIX = 'lifecycle:';
const RENEWAL_STATE_PREFIX = 'renewal:';

/**
 * Filtre « État » : la liste affiche le cycle de vie d'un produit offert et
 * l'état propre d'une remise renouvellement (`07` §6) ; chaque valeur ne vise
 * que son type d'opération.
 */
function stateFilterFromValue(value: string): Pick<OperationsFiltersValue, 'lifecycle' | 'renewalState'> {
  const lifecycle = operationLifecycleSchema.safeParse(value.slice(LIFECYCLE_PREFIX.length));
  if (value.startsWith(LIFECYCLE_PREFIX) && lifecycle.success) return { lifecycle: lifecycle.data, renewalState: undefined };
  const renewalState = renewalDiscountStateSchema.safeParse(value.slice(RENEWAL_STATE_PREFIX.length));
  if (value.startsWith(RENEWAL_STATE_PREFIX) && renewalState.success) return { lifecycle: undefined, renewalState: renewalState.data };
  return { lifecycle: undefined, renewalState: undefined };
}

interface OperationsFiltersValue {
  search?: string | undefined;
  lifecycle?: OperationLifecycle | undefined;
  renewalState?: RenewalDiscountState | undefined;
  kind?: OperationKind | undefined;
  archived: boolean;
}

function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/** Lit les filtres de l'écran Opérations depuis `useSearch({strict:false})` (`02` §10). */
function readOperationsFilters(rawSearch: unknown): OperationsFiltersValue {
  const search = readRecord(rawSearch);
  const lifecycle = operationLifecycleSchema.safeParse(search.lifecycle);
  const renewalState = renewalDiscountStateSchema.safeParse(search.renewalState);
  const kind = operationKindSchema.safeParse(search.kind);
  return {
    ...(lifecycle.success ? { lifecycle: lifecycle.data } : {}),
    ...(renewalState.success ? { renewalState: renewalState.data } : {}),
    ...(kind.success ? { kind: kind.data } : {}),
    archived: search.archived === true,
    ...(typeof search.search === 'string' ? { search: search.search } : {}),
  };
}

const helper = createDataTableColumnHelper<OperationListItem>();

const columns = helper.columns([
  helper.accessor('name', {
    id: 'name',
    header: 'Nom',
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  helper.accessor('kind', {
    id: 'kind',
    header: 'Type',
    cell: (info) => <StatusBadge registry={OPERATION_KIND} code={info.getValue()} />,
  }),
  helper.display({
    id: 'period',
    header: 'Période',
    cell: ({ row }) => {
      const { period } = row.original;
      return (
        <span className="whitespace-nowrap tabular-nums">
          {formatParisDate(new Date(period.start))}
          {period.end === null ? ' → sans fin' : ` → ${formatParisDate(new Date(period.end))}`}
        </span>
      );
    },
  }),
  helper.display({
    id: 'stores',
    header: 'Origines',
    // Une remise renouvellement porte sur les abonnements Recharge, pas sur des origines.
    cell: ({ row }) =>
      row.original.kind === 'renewal_discount' ? (
        <span className="text-muted-foreground">Abonnements</span>
      ) : (
        <OriginBadges codes={row.original.stores} />
      ),
  }),
  helper.accessor('lifecycle', {
    id: 'lifecycle',
    header: 'État',
    // Une remise renouvellement a son propre cycle (`07` §4.3) ; archivée, elle reste « Archivée ».
    cell: ({ row }) =>
      row.original.kind === 'renewal_discount' && row.original.renewalState !== null && !row.original.archived ? (
        <StatusBadge registry={RENEWAL_DISCOUNT_STATE} code={row.original.renewalState} />
      ) : (
        <StatusBadge registry={OPERATION_LIFECYCLE} code={row.original.lifecycle} />
      ),
  }),
  helper.accessor('matchedOrdersCount', {
    id: 'matchedOrdersCount',
    header: 'Commandes',
    // Une remise renouvellement ne touche aucune commande exportée.
    cell: ({ row }) =>
      row.original.kind === 'renewal_discount' ? (
        <span className="text-muted-foreground">
          <span aria-hidden="true">—</span>
          <span className="sr-only">Sans objet</span>
        </span>
      ) : (
        row.original.matchedOrdersCount
      ),
  }),
]);

/** Écran Opérations commerciales — `02` §10. */
export function OperationsPage(): JSX.Element {
  const navigate = useNavigate();
  const rawSearch = useSearch({ strict: false });
  const { page, sortBy, sortDir, setPage, setSort, setFilters } = useDataTableUrlState();
  const filters = readOperationsFilters(rawSearch);

  const listParams = {
    page,
    pageSize: PAGE_SIZE,
    sortDir,
    ...(sortBy ? { sortBy } : {}),
    ...(filters.lifecycle !== undefined ? { lifecycle: filters.lifecycle } : {}),
    ...(filters.renewalState !== undefined ? { renewalState: filters.renewalState } : {}),
    ...(filters.kind !== undefined ? { kind: filters.kind } : {}),
    ...(filters.archived ? { archived: true } : {}),
    ...(filters.search ? { search: filters.search } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.operations.list(listParams),
    queryFn: () => fetchOperationsList(listParams),
  });

  const isFiltered = filters.lifecycle !== undefined || filters.renewalState !== undefined || filters.kind !== undefined || filters.archived;
  const hasCriteria = isFiltered || filters.search !== undefined;

  function resetAll(): void {
    setFilters({ lifecycle: undefined, renewalState: undefined, kind: undefined, archived: undefined, search: undefined });
  }

  return (
    <Page>
      <PageHeader
        title="Opérations commerciales"
        description="Ajoutez automatiquement un produit aux commandes, ou posez une remise sur le prochain renouvellement d’abonnement, pendant une période donnée."
        actions={
          <Button asChild>
            <Link to="/operations/nouvelle" search={{}}>
              <Plus aria-hidden="true" />
              Nouvelle opération
            </Link>
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        sortBy={sortBy}
        sortDir={sortDir}
        sortableColumnIds={SORTABLE_COLUMNS}
        numericColumnIds={NUMERIC_COLUMNS}
        onPageChange={setPage}
        onSortChange={setSort}
        isLoading={query.isPending}
        isError={query.isError}
        {...(query.error instanceof ApiError ? { errorMessage: query.error.message } : {})}
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        onRowClick={(row) => {
          void navigate({ to: `/operations/${row.id}` });
        }}
        toolbar={
          <ListToolbar
            search={{
              value: filters.search ?? '',
              onChange: (next) => {
                setFilters({ search: next === '' ? undefined : next });
              },
              placeholder: 'Nom de l’opération…',
            }}
            isFiltered={isFiltered}
            onReset={() => {
              setFilters({ lifecycle: undefined, renewalState: undefined, kind: undefined, archived: undefined });
            }}
          >
            <Select
              value={
                filters.renewalState !== undefined
                  ? `${RENEWAL_STATE_PREFIX}${filters.renewalState}`
                  : filters.lifecycle !== undefined
                    ? `${LIFECYCLE_PREFIX}${filters.lifecycle}`
                    : ALL_LIFECYCLES
              }
              onValueChange={(next) => {
                setFilters(stateFilterFromValue(next));
              }}
            >
              <SelectTrigger className="w-48" aria-label="État">
                <SelectValue placeholder="Tous les états" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_LIFECYCLES}>Tous les états</SelectItem>
                <SelectGroup>
                  <SelectLabel>{OPERATION_KIND.free_product.label}</SelectLabel>
                  {statusEntries(OPERATION_LIFECYCLE)
                    .filter((entry) => entry.code !== 'archived')
                    .map((entry) => (
                      <SelectItem key={entry.code} value={`${LIFECYCLE_PREFIX}${entry.code}`}>
                        {entry.label}
                      </SelectItem>
                    ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>{OPERATION_KIND.renewal_discount.label}</SelectLabel>
                  {statusEntries(RENEWAL_DISCOUNT_STATE).map((entry) => (
                    <SelectItem key={entry.code} value={`${RENEWAL_STATE_PREFIX}${entry.code}`}>
                      {entry.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <Select
              value={filters.kind ?? ALL_KINDS}
              onValueChange={(next) => {
                setFilters({ kind: next === ALL_KINDS ? undefined : next });
              }}
            >
              <SelectTrigger className="w-52" aria-label="Type">
                <SelectValue placeholder="Tous les types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_KINDS}>Tous les types</SelectItem>
                {statusEntries(OPERATION_KIND).map((entry) => (
                  <SelectItem key={entry.code} value={entry.code}>
                    {entry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <label className="flex h-9 items-center gap-2 px-1 text-sm">
              <Checkbox
                checked={filters.archived}
                onCheckedChange={(checked) => {
                  setFilters({ archived: checked === true ? true : undefined });
                }}
              />
              Afficher les archivées
            </label>
          </ListToolbar>
        }
        emptyState={
          hasCriteria
            ? {
                title: 'Aucune opération ne correspond à ces filtres',
                description: 'Modifiez la recherche ou les filtres, ou réinitialisez-les.',
                action: (
                  <Button type="button" variant="outline" onClick={resetAll}>
                    Réinitialiser les filtres
                  </Button>
                ),
              }
            : {
                title: 'Aucune opération commerciale',
                description:
                  'Créez une opération pour ajouter automatiquement un produit à certaines commandes, ou pour poser une remise sur le prochain renouvellement d’abonnement.',
                action: (
                  <Button asChild variant="outline">
                    <Link to="/operations/nouvelle" search={{}}>
                      Nouvelle opération
                    </Link>
                  </Button>
                ),
              }
        }
      />
    </Page>
  );
}
